import React, { useState, useMemo, useEffect } from "react";
import { GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import { FormSectionElements } from "../../entities/formTemplateDataType";
import { getEnvVar } from "../../config/RuntimeConfig";

const containerStyle = {
  width: "100%",
  height: "400px",
  minWidth: "300px",
  resize: "both" as const,
  overflow: "auto",
  border: "1px solid #ccc",
};

const searchBarStyle = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  fontSize: "14px",
  backgroundColor: "#fff",
  outline: "none",
};

const suggestionListStyle = {
  position: "absolute" as const,
  zIndex: 1000,
  width: "100%",
  backgroundColor: "#fff",
  border: "1px solid #ccc",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  maxHeight: "200px",
  overflowY: "auto" as const,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
};

const suggestionItemStyle = {
  padding: "8px 12px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f0f0",
};

const suggestionItemHoverStyle = {
  backgroundColor: "#f5f5f5",
};

const defaultCenter = {
  lat: 43.6532, // Toronto
  lng: -79.3832,
};

export type GoogleMapProps = {
  field: FormSectionElements;
  labelControlArray?: any[];
  isEng: boolean;
  callbackElement: any;
}

interface MapLocation {
  id: string;
  name: string;
  nameFRA: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  descriptionFRA?: string;
}

function GoogleMapComponent({ field, isEng, labelControlArray, callbackElement }: GoogleMapProps) {
  const [selectedMarker, setSelectedMarker] = useState<MapLocation | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [hoveredSuggestion, setHoveredSuggestion] = useState<number>(-1);
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  
  const apiKey = getEnvVar('GOOGLE_MAPS_API_KEY');
  const locations: MapLocation[] = (field as any).mapLocations || [];

  if (!apiKey) {
    return (
      <div style={{ color: "#e74c3c", padding: "20px", textAlign: "center" }}>
        {isEng 
          ? "Google Maps API key is missing. Please set the GOOGLE_MAPS_API_KEY environment variable."
          : "La clé API Google Maps est manquante. Veuillez définir la variable d'environnement GOOGLE_MAPS_API_KEY."
        }
      </div>
    );
  }

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    
    const query = searchQuery.toLowerCase();
    return locations.filter(location => {
      const name = isEng ? location.name : location.nameFRA;
      const description = isEng ? location.description : location.descriptionFRA;
      
      return name.toLowerCase().includes(query) ||
        location.address.toLowerCase().includes(query) ||
        (description && description.toLowerCase().includes(query));
    });
  }, [locations, searchQuery, isEng]);

  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    
    const query = searchQuery.toLowerCase();
    return locations.filter(location => {
      const name = isEng ? location.name : location.nameFRA;
      const description = isEng ? location.description : location.descriptionFRA;
      
      return name.toLowerCase().includes(query) ||
        location.address.toLowerCase().includes(query) ||
        (description && description.toLowerCase().includes(query));
    }).slice(0, 5);
  }, [locations, searchQuery, isEng]);
  
  const getMapCenter = () => {
    if (locations.length === 0) return defaultCenter;
    
    if (locations.length === 1) {
      return { lat: locations[0].lat, lng: locations[0].lng };
    }
    
    const avgLat = locations.reduce((sum, loc) => sum + loc.lat, 0) / locations.length;
    const avgLng = locations.reduce((sum, loc) => sum + loc.lng, 0) / locations.length;
    
    return { lat: avgLat, lng: avgLng };
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const handleMarkerClick = (location: MapLocation) => {
    setSelectedMarker(location);
  };

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
    setIsSearchActive(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(value.length >= 2);
    setHoveredSuggestion(-1);
    setIsSearchActive(value.length > 0);
  };

  const handleSuggestionClick = (location: MapLocation) => {
    const displayName = isEng ? location.name : location.nameFRA;
    setSearchQuery(displayName);
    setShowSuggestions(false);
    setSelectedMarker(location);
  };

  const handleSearchFocus = () => {
    setIsSearchExpanded(true);
    setIsSearchActive(true);
    if (searchQuery.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      if (!searchQuery.trim()) {
        setIsSearchExpanded(false);
        setIsSearchActive(false);
      }
    }, 200);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setShowSuggestions(false);
    setSelectedMarker(null);
    setIsSearchExpanded(false);
    setIsSearchActive(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearSearch();
      return;
    }

    if (!showSuggestions || searchSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHoveredSuggestion(prev => 
          prev < searchSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHoveredSuggestion(prev => 
          prev > 0 ? prev - 1 : searchSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (hoveredSuggestion >= 0 && hoveredSuggestion < searchSuggestions.length) {
          handleSuggestionClick(searchSuggestions[hoveredSuggestion]);
        }
        break;
    }
  };

  return (
    <div className="google-map-component" style={{ width: "100%" }}>
      <LoadScript 
        googleMapsApiKey={apiKey}
        libraries={['places']}
        loadingElement={
          <div style={{ 
            height: "400px",
            width: "100%",
            minWidth: "300px",
            border: "1px solid #ccc", 
            borderRadius: 4, 
            background: "#f7f7f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666"
          }}>
            {isEng ? "Loading map..." : "Chargement de la carte..."}
          </div>
        }
      >
        <div style={{ position: "relative", width: "100%" }}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={getMapCenter()}
            zoom={locations.length <= 1 ? 12 : 10}
            onLoad={handleMapLoad}
            options={{
              disableDefaultUI: isSearchActive,
              zoomControl: !isSearchActive,
              streetViewControl: false,
              mapTypeControl: !isSearchActive,
              mapTypeControlOptions: {
                position: window.google?.maps?.ControlPosition?.TOP_RIGHT || 1,
              },
              fullscreenControl: !isSearchActive,
              fullscreenControlOptions: {
                position: window.google?.maps?.ControlPosition?.TOP_RIGHT || 1,
              },
            }}
          >
            {locations.map((location) => (
              <Marker
                key={location.id}
                position={{ lat: location.lat, lng: location.lng }}
                onClick={() => handleMarkerClick(location)}
                title={isEng ? location.name : location.nameFRA}
              />
            ))}

            {selectedMarker && (
              <InfoWindow
                position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div style={{ maxWidth: '200px' }}>
                  <h6 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
                    {isEng ? selectedMarker.name : selectedMarker.nameFRA}
                  </h6>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px' }}>
                    {selectedMarker.address}
                  </p>
                  {((isEng && selectedMarker.description) || (!isEng && selectedMarker.descriptionFRA)) && (
                    <p style={{ margin: '0', fontSize: '11px', fontStyle: 'italic' }}>
                      {isEng ? selectedMarker.description : selectedMarker.descriptionFRA}
                    </p>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>

          <div
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              transition: "all 0.3s ease",
              width: isSearchExpanded ? "min(360px, calc(100% - 140px))" : "40px",
              height: "40px",
            }}
          >
            {!isSearchExpanded ? (
              <button
                onClick={handleSearchIconClick}
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 6px rgba(32,33,36,0.28)",
                  fontSize: "16px",
                  color: "#666",
                }}
                title={isEng ? "Search locations" : "Rechercher des emplacements"}
              >
                <i className="pi pi-search" style={{ fontSize: "1.5rem" }}></i>
              </button>
            ) : (
              <div style={{ position: "relative", height: "40px" }}>
                <input
                  type="text"
                  placeholder={isEng ? "Search locations..." : "Rechercher des emplacements..."}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  style={{ 
                    ...searchBarStyle, 
                    height: "40px",
                    marginBottom: 0, 
                    boxShadow: "0 1px 6px rgba(32,33,36,0.28)",
                    fontSize: "clamp(12px, 2.5vw, 14px)",
                    paddingRight: "40px",
                  }}
                />
                <button
                  onClick={clearSearch}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#666",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title={isEng ? "Close search" : "Fermer la recherche"}
                >
                  <i className="pi pi-times" style={{ fontSize: '0.5rem' }}></i>
                </button>

                {showSuggestions && searchSuggestions.length > 0 && (
                  <div style={suggestionListStyle}>
                    {searchSuggestions.map((location, index) => (
                      <div
                        key={location.id}
                        style={{
                          ...suggestionItemStyle,
                          ...(index === hoveredSuggestion ? suggestionItemHoverStyle : {}),
                        }}
                        onClick={() => handleSuggestionClick(location)}
                        onMouseEnter={() => setHoveredSuggestion(index)}
                      >
                        <div style={{ fontWeight: "bold", fontSize: "clamp(11px, 2vw, 12px)" }}>
                          {isEng ? location.name : location.nameFRA}
                        </div>
                        <div style={{ fontSize: "clamp(10px, 1.8vw, 11px)", color: "#666" }}>
                          {location.address}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </LoadScript>

      {searchQuery && (
        <div style={{ margin: "10px 0", fontSize: "clamp(11px, 2vw, 12px)", color: "#666" }}>
          {isEng
            ? `Showing ${filteredLocations.length} of ${locations.length} locations`
            : `Affichage de ${filteredLocations.length} sur ${locations.length} emplacements`}
          {filteredLocations.length === 0 && (
            <span style={{ color: "#e74c3c", marginLeft: "8px" }}>
              {isEng ? "No matches found" : "Aucun résultat trouvé"}
            </span>
          )}
        </div>
      )}

      {locations.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          {isEng 
            ? "No locations configured for this map"
            : "Aucun emplacement configuré pour cette carte"
          }
        </div>
      )}
    </div>
  );
}

export default GoogleMapComponent;